import { useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Archive, Eye, Plus } from 'lucide-react';
import { Button, Label } from '@artstorefronts/ui';
import type { ExplainerCtaConfig } from '@arthelper/shared/feature-wizard-config';
import AdminLayout from '../../../components/AdminLayout';
import { GET_FEATURE_WIZARD, GET_FEATURE_WIZARDS } from '../../../graphql/queries';
import { SET_FEATURE_WIZARD_STATUS } from '../../../graphql/mutations';
import { toStepConfig, wizardParameter } from './draft';
import { CopyButton } from './CopyButton';
import { LinkBuilder } from './LinkBuilder';
import { WizardPreviewModal } from './PreviewModal';
import type { WizardListRow, WizardRecord, WizardStatus } from './types';

const STATUS_PILLS: Record<WizardStatus, string> = {
  PUBLISHED: 'bg-green-100 text-green-800',
  DRAFT: 'bg-gray-100 text-gray-700',
  ARCHIVED: 'bg-amber-100 text-amber-800',
};

const STATUS_LABELS: Record<WizardStatus, string> = {
  PUBLISHED: 'Published',
  DRAFT: 'Draft',
  ARCHIVED: 'Archived',
};

const HEADINGS = ['Slug', 'Name', 'Status', 'Steps', 'Control %', 'Parameter'];

function FeatureWizardsPage() {
  const { data, loading, refetch } = useQuery<{ featureWizards: WizardListRow[] }>(GET_FEATURE_WIZARDS);
  const [setStatus, { loading: settingStatus }] = useMutation(SET_FEATURE_WIZARD_STATUS);

  // Fetched on click, never folded into the list query — the list stays as cheap as it is.
  const [loadWizard, { loading: loadingPreview }] = useLazyQuery<{ featureWizard: WizardRecord | null }>(
    GET_FEATURE_WIZARD,
    { fetchPolicy: 'network-only' }
  );

  const [previewSteps, setPreviewSteps] = useState<ExplainerCtaConfig[] | null>(null);
  const [builderSlug, setBuilderSlug] = useState('');

  const wizards = data?.featureWizards ?? [];

  const handlePreview = async (id: string) => {
    try {
      const result = await loadWizard({ variables: { id } });
      const wizard = result.data?.featureWizard;
      if (!wizard || wizard.steps.length === 0) return;
      setPreviewSteps([...wizard.steps].sort((a, b) => a.position - b.position).map((s) => toStepConfig(s.config)));
    } catch (error) {
      console.error('Error loading wizard preview:', error);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await setStatus({ variables: { id, status: 'ARCHIVED' } });
      await refetch();
    } catch (error) {
      console.error('Error archiving wizard:', error);
    }
  };

  return (
    <AdminLayout
      title="Feature wizards"
      description="Short guided flows you can drop into Intercom or SMS as a link. A wizard is not tied to a page: append its parameter to whatever URL the campaign is already sending, including a different one per recipient. Editing a wizard never changes its parameter."
      action={
        <Button variant="default" size="sm" className="gap-1.5 bg-gray-900 hover:bg-gray-800" asChild>
          <Link to="/admin/feature-wizards/new">
            <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
            New wizard
          </Link>
        </Button>
      }
    >
      {loading ? (
        <div className="text-gray-500">Loading wizards…</div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {HEADINGS.map((heading) => (
                    <th
                      key={heading}
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {heading}
                    </th>
                  ))}
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {wizards.map((wizard) => (
                  <tr key={wizard.id}>
                    <td className="whitespace-nowrap px-6 py-4 font-mono text-xs text-gray-900">{wizard.slug}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{wizard.name}</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_PILLS[wizard.status]}`}>
                        {STATUS_LABELS[wizard.status]}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{wizard.steps.length}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{wizard.controlPercent}%</td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-800">
                          {wizardParameter(wizard.slug)}
                        </code>
                        <CopyButton
                          value={wizardParameter(wizard.slug)}
                          ariaLabel={`Copy the parameter for ${wizard.slug}`}
                        />
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handlePreview(wizard.id)}
                          disabled={loadingPreview}
                          aria-label={`Preview the ${wizard.slug} wizard`}
                          title="Preview this wizard"
                          className="text-gray-400 hover:text-gray-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="font-normal" asChild>
                          <Link to={`/admin/feature-wizards/${wizard.id}`}>Edit</Link>
                        </Button>
                        {/* A link, never a count: the aggregate runs on the wizard's own page. */}
                        <Button variant="outline" size="sm" className="font-normal" asChild>
                          <Link to={`/admin/feature-wizards/${wizard.id}?tab=results`}>Results</Link>
                        </Button>
                        {wizard.status !== 'ARCHIVED' && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-1.5 font-normal text-red-700 hover:bg-red-50 hover:text-red-800"
                            onClick={() => handleArchive(wizard.id)}
                            disabled={settingStatus}
                          >
                            <Archive className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            Archive
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {wizards.length === 0 && (
            <p className="px-6 py-8 text-center text-sm text-gray-500">
              No wizards yet. Create one — nothing is live and no link works until you publish it.
            </p>
          )}
        </div>
      )}

      {wizards.length > 0 && (
        <div className="mt-6 rounded-lg bg-white p-6 shadow">
          <h3 className="text-base font-semibold">Build a finished link</h3>
          <p className="mt-1 text-sm text-gray-500">
            The table copies the parameter, because a wizard opens on whatever page the link is sent to. Pick a wizard
            and paste a URL to get the whole link back.
          </p>
          <Label htmlFor="wizard-link-builder-slug" className="mt-3 block text-sm font-medium">
            Wizard
          </Label>
          <select
            id="wizard-link-builder-slug"
            className="mt-1 w-full max-w-sm rounded-md border border-gray-300 px-2 py-1.5 font-mono text-sm"
            value={builderSlug}
            onChange={(event) => setBuilderSlug(event.target.value)}
          >
            <option value="">Choose a wizard…</option>
            {wizards.map((wizard) => (
              <option key={wizard.id} value={wizard.slug}>
                {wizard.slug}
              </option>
            ))}
          </select>
          <LinkBuilder slug={builderSlug} />
        </div>
      )}

      {previewSteps && (
        <WizardPreviewModal
          steps={previewSteps}
          initialStepIndex={0}
          source="saved"
          onClose={() => setPreviewSteps(null)}
        />
      )}
    </AdminLayout>
  );
}

export default FeatureWizardsPage;
